import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Stack, Box, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CloseDialogAction } from './CloseDialogAction';
import { TransactionValidationConflict, TransactionValidationConflictType } from '../../../app/services/api/transactionValidationTypes';
import dayjs from 'dayjs';

const formatDate = (value: string) => (value ? dayjs(value).format('DD MMM YYYY') : '');

interface TransactionValidationDialogProps {
  readonly open: boolean;
  readonly mode: 'Warn' | 'Block';
  readonly conflicts: TransactionValidationConflict[];
  readonly onContinue: () => void;
  readonly onCancel: () => void;
}

const conflictMeta: Record<TransactionValidationConflictType, { labelKey: string; actionKey: string; route: (conflict: TransactionValidationConflict) => string }> = {
  ApprovedCount: {
    labelKey: 'transactionValidation.conflictApprovedCount',
    actionKey: 'transactionValidation.actionViewStockCounts',
    route: (conflict) => (conflict.reference ? `/store/review-stock-count/${conflict.reference}` : '/store/stock-count'),
  },
  PendingCount: {
    labelKey: 'transactionValidation.conflictPendingCount',
    actionKey: 'transactionValidation.actionApproveCount',
    route: (conflict) => (conflict.reference ? `/store/review-stock-count/${conflict.reference}` : '/store/stock-count'),
  },
  OutstandingReceipt: {
    labelKey: 'transactionValidation.conflictOutstandingReceipt',
    actionKey: 'transactionValidation.actionAcceptReceipt',
    route: (conflict) => (conflict.referenceId ? `/create-goods-receipt/${conflict.referenceId}` : '/store/goods-receipts'),
  },
};

export const TransactionValidationDialog = ({ open, mode, conflicts, onContinue, onCancel }: TransactionValidationDialogProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const isBlock = mode === 'Block';

  const goTo = (route: string) => {
    onCancel();
    navigate(route);
  };

  const buildLabel = (conflict: TransactionValidationConflict, labelKey: string) => {
    const base = t(labelKey);
    if (conflict.conflictType === 'OutstandingReceipt') {
      const reference = conflict.reference ? ` — ${t('transactionValidation.conflictPoReference', { reference: conflict.reference })}` : '';
      const due = conflict.date ? ` (${t('transactionValidation.conflictDue', { date: formatDate(conflict.date) })})` : '';
      return `${base}${reference}${due}`;
    }
    const reference = conflict.reference ? ` #${conflict.reference}` : '';
    const date = conflict.date ? ` — ${formatDate(conflict.date)}` : '';
    return `${base}${reference}${date}`;
  };

  const hasCount = conflicts.some((c) => c.conflictType === 'ApprovedCount' || c.conflictType === 'PendingCount');
  const hasReceipt = conflicts.some((c) => c.conflictType === 'OutstandingReceipt');
  const suffix = hasCount && hasReceipt ? 'Mixed' : hasReceipt ? 'Receipt' : '';

  const title = isBlock ? t('transactionValidation.blockTitle') : t(`transactionValidation.warnTitle${suffix}`);
  const intro = isBlock ? t(`transactionValidation.blockIntro${suffix}`) : t(`transactionValidation.warnIntro${suffix}`);

  return (
    <Dialog onClose={onCancel}
      open={open}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>
        {title}
        <CloseDialogAction onCancel={onCancel} />
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>{intro}</Typography>
        <Stack gap="8px">
          {conflicts.map((conflict, index) => {
            const meta = conflictMeta[conflict.conflictType];
            return (
              <Box key={`${conflict.itemNumber}-${conflict.conflictType}-${index}`}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: '8px 12px' }}>
                <Box>
                  <Typography variant="textSM"
                    sx={{ fontWeight: 600 }}>{conflict.itemNumber}</Typography>
                  <Typography variant="textXS"
                    sx={{ display: 'block' }}>{meta ? buildLabel(conflict, meta.labelKey) : conflict.conflictType}</Typography>
                </Box>
                {meta &&
                  <Link component="button"
                    type="button"
                    onClick={() => goTo(meta.route(conflict))}
                    sx={{ whiteSpace: 'nowrap' }}>
                    {t(meta.actionKey)}
                  </Link>}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction={{ xs: 'column', md: 'row' }}
          gap="6px"
          width="100%">
          {!isBlock &&
            <Button variant="badSecondary"
              fullWidth
              size="lg"
              onClick={onContinue}>
              {t('transactionValidation.continueAnyway')}
            </Button>}
          <Button variant="primary"
            fullWidth
            size="lg"
            onClick={onCancel}>
            {isBlock ? t('transactionValidation.close') : t('transactionValidation.cancel')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
